import { useEffect, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { labelSizeForPreview, zplPreviewBody, zplPrintsUpsideDown } from '@/lib/labelPrinting'
import { renderZpl } from './zplRender'

const MM_PER_INCH = 25.4

type Drawn = { status: 'drawing' } | { status: 'drawn'; src: string } | { status: 'failed' }

/**
 * What a Zebra layout looks like, drawn in the browser with made-up values in
 * place of each placeholder. Seeded layouts start with a printer setup job
 * that draws nothing, so the last label in the job is the one shown.
 *
 * `placeholders` is undefined while the list of samples is still loading.
 * `delayMs` waits for typing to pause before drawing again.
 */
export function ZplPreview({
  body,
  labelStock,
  resolution,
  placeholders,
  delayMs = 0,
  caption = 'Preview with made-up values. The printed label may differ slightly.',
}: {
  body: string
  labelStock: string
  resolution: string
  placeholders: { name: string; sample: string }[] | undefined
  delayMs?: number
  /** The line under the drawn label. */
  caption?: ReactNode
}) {
  const [drawn, setDrawn] = useState<Drawn>({ status: 'drawing' })
  const size = labelSizeForPreview(labelStock, resolution)
  const zpl = placeholders ? zplPreviewBody(body, placeholders) : null
  const widthIn = size?.widthIn
  const heightIn = size?.heightIn
  const dpmm = size?.dpmm

  useEffect(() => {
    if (zpl === null || widthIn === undefined || heightIn === undefined || dpmm === undefined) return
    let current = true
    const timer = setTimeout(() => {
      // A new draw starts: drop the last image or failure until it is done.
      setDrawn({ status: 'drawing' })
      renderZpl(zpl, { widthMm: widthIn * MM_PER_INCH, heightMm: heightIn * MM_PER_INCH, dpmm })
        .then(images => {
          const last = images[images.length - 1]
          if (!last) throw new Error('The renderer returned no label.')
          if (current) setDrawn({ status: 'drawn', src: `data:image/png;base64,${last}` })
        })
        .catch((err: unknown) => {
          if (!current) return
          console.error('Zebra label preview failed', err)
          setDrawn({ status: 'failed' })
        })
    }, delayMs)
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [zpl, widthIn, heightIn, dpmm, delayMs])

  if (!size || drawn.status === 'failed') {
    return (
      <p role='status' className='rounded-lg border border-dashed px-3 py-4 text-sm text-muted-foreground'>
        The preview could not be drawn. Check the layout.
      </p>
    )
  }

  return (
    <figure className='flex flex-col gap-1.5'>
      {drawn.status === 'drawn' ? (
        <div className='overflow-hidden rounded-md border bg-white shadow-sm'>
          <img src={drawn.src} alt='Label preview' className='block h-auto w-full' />
        </div>
      ) : (
        <div
          role='status'
          className='flex items-center justify-center gap-2 rounded-md border border-dashed bg-muted/30 text-sm text-muted-foreground'
          style={{ aspectRatio: `${size.widthIn} / ${size.heightIn}` }}
        >
          <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
          Drawing the preview…
        </div>
      )}
      {drawn.status === 'drawn' && (
        <figcaption className='text-xs text-muted-foreground'>
          {caption}
          {zplPrintsUpsideDown(body) && <span className='block'>Shown upright. It prints upside down on the roll.</span>}
        </figcaption>
      )}
    </figure>
  )
}
